// ================ router.query との相互作用 ================
// useRoute, useRouter を含むため composable としての利用
import type { LocationQuery, LocationQueryValue } from "vue-router";
import type { Booth, MainViewMode, MapArea, QueryFavorite, ComikeMapType, PrintParam, MapSearchCategory, CircleSearchParam, ViewMode } from "~/types/entity/cWebCatalog";
import { isViewMode } from "~/types/entity/cWebCatalog";
import { toPrintType } from "~/types/entity/printModel";
import { useCircleSearchState } from "~/composables/state/useCircleSearchState";
import { useBoothSearchState } from "~/composables/state/useBoothSearchState";
import { getNavigationRoute } from "~/utils/urlHelper";
import { clampMemoLines } from "~/utils/printListLayout";
import { clampRankingScoreThreshold, toSortOption } from "~/utils/circleSearchHelper";

/**
 * Navigation から呼ばれるページ遷移のためのメソッド
 */
export const changeMainViewMode = async (mode: MainViewMode) => {
  const nextRoute = getNavigationRoute(mode); // 遷移先のページRoute (/catalog など)
  const route = useRoute();
  const mainViewMode = useMainViewModeState();
  const userModel = useUserModel();
  const checkPermission = await userModel.checkPermission(mode);
  // console.log("checkPermission", checkPermission)
  switch (checkPermission) {
    case true: // 権限あり
      // 権限ありでここに到達したということは ログインのためのリダイレクトが不要になったタイミングである
      (useInitialRouteState()).clearInitialRouteFullpath();
      if (nextRoute !== undefined && route.path.toLowerCase() !== nextRoute?.toLowerCase()) {
        await navigateToRoute(mode);
      }
      mainViewMode.value = mode;
      // mainViewModeToQuery(mode);
      return;
    case false:
      return; // 表示モードは変えない
    default: // undefined = 未ログイン
      await navigateToRoute("LOGIN");
      return;
  }
};

/**
 * 単一の文字列を取得する。配列だった場合にはカンマで結合する
 * @param v
 * @returns
 */
const getSingleString = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): string | undefined => {
  if (Array.isArray(v)) {
    return v.join(",");
  }
  if (typeof v === "string") {
    return v;
  }
  return undefined;
};

/**
 * 数値配列に変換する NaN は含めない
 * @param v‘
 * @returns
 */
const getMultiNumber = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): number[] | undefined =>
  getMultiStringCommaSplit(v)?.map(x => Number(x)).filter(x => !isNaN(x));

/**
 * 文字列配列に変換する。単一の値だった場合カンマ区切りで分離した配列とする。ただし値がなかった場合は undefined
 * @param v
 * @returns
 */
const getMultiStringCommaSplit = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): string[] | undefined => {
  if (Array.isArray(v)) {
    return v.map(item => (typeof item === "string" ? item : "")).filter(item => item !== "");
  }
  if (typeof v === "string") {
    return v.split(",");
  }
  return undefined;
};

const getNumber = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): number | undefined => {
  if (Array.isArray(v)) {
    const n = Number(v[0]);
    return isNaN(n) ? undefined : n;
  }
  if (typeof v === "string") {
    const n = Number(v);
    return isNaN(n) ? undefined : n;
  }
  return undefined;
};

const getBool = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): boolean | undefined => {
  switch(v) {
    case "0": return false;
    case "1": return true;
    default: return undefined;
  }
};

/**
 * "Day1" と固定でDay文字列を正規表現を用いて除去して 数値 を取得する
 * @param v "Day1"
 */
const getDayNumber = (v: string | undefined): number | undefined => {
  const match = v?.match(/Day(\d+)/i);
  if (match) {
    const day = Number(match[1]);
    return isNaN(day) ? undefined : day;
  }
  return undefined;
};

export const getQueryFavoriteFromQueryString = (defaultDay: number): QueryFavorite | undefined => {
  const route = useRoute();
  return ({
    tab: getNumber(route.query.tab) ?? 1,
    favorite: getNumber(route.query.favorite),
    day: getNumber(route.query.day) ?? defaultDay,
    sort: getSingleString(route.query.sort)
  });
};

// query の string から CircleListの viewMode への変換をする。
// ViewMode に該当しない値は undefined とする。
const getCircleViewMode = (viewStr: string | undefined): ViewMode | undefined =>
  isViewMode(viewStr) ? viewStr : undefined;

// const mainViewModeToNumber = (mainViewMode: MainViewMode): number => {
//   switch (mainViewMode) {
//     case "FAVORITE":
//       return MainViewModeQueryObject.FAVORITE;
//     case "BOOTH_LIST":
//       return MainViewModeQueryObject.BOOTH_LIST;
//     case "COMIKE_MAP":
//       return MainViewModeQueryObject.COMIKE_MAP;
//     case "COMIKE_PRINT":
//       return MainViewModeQueryObject.COMIKE_PRINT;
//     case "CIRCLE_LIST":
//     default:
//       return MainViewModeQueryObject.CIRCLE_LIST;
//   }
// };

// const mainViewModeFromNumber = (query: MainViewModeQuery | number): MainViewMode => {
//   switch (query) {
//     case MainViewModeQueryObject.TOP_PAGE:
//       return "TOP_PAGE";
//     case MainViewModeQueryObject.BOOTH_LIST:
//       return "BOOTH_LIST";
//     case MainViewModeQueryObject.FAVORITE:
//       return "FAVORITE";
//     case MainViewModeQueryObject.COMIKE_MAP:
//       return "COMIKE_MAP";
//     case MainViewModeQueryObject.COMIKE_PRINT:
//       return "COMIKE_PRINT";
//     case MainViewModeQueryObject.CIRCLE_LIST:
//     case MainViewModeQueryObject.UNKNOWN:
//     default:
//       return "CIRCLE_LIST";
//   }
// };

/**
 * MainViewMode を query の view に設定する
 */
// const mainViewModeToQuery = (mainViewMode: MainViewMode) => {
//   const router = useRouter();
//   router.push({
//     query: {
//       view: mainViewModeToNumber(mainViewMode)
//     }
//   });
// };

/**
 * Query 中の view に設定された値から MainViewMode を取得する
 * @returns
 */
// export const mainViewModeFromQuery = (): MainViewMode => {
//   const route = useRoute();
//   const query = route.query.view ? route.query.view : MainViewModeQueryObject.UNKNOWN;
//   return mainViewModeFromNumber(Number(query));
// };

/**
 * @param replace true の場合は履歴を追加せず現在の履歴を置き換える (スクロール位置の反映など)
 */
export const updateQueryCircleList = (replace = false):LocationQuery => {
  const router = useRouter();
  const param: LocationQuery = getLocationQuery();
  const location = {
    query: {
      // view: MainViewModeQueryObject.CIRCLE_LIST,
      ...param
    }
  };
  if (replace) {
    router.replace(location);
  } else {
    router.push(location);
  }
  return param;
};
export const updateQueryBoothList = ():LocationQuery => {
  const router = useRouter();
  const keyword = useBoothSearchState().get().keyword;
  router.push({
    query: {
      // view: MainViewModeQueryObject.BOOTH_LIST,
      text: keyword
    }
  });
  return { text: keyword } as LocationQuery; // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
};
export const updateQueryFavorite = () => {
  const router = useRouter();
  const query: QueryFavorite = (useQueryFavoriteState()).get();
  router.push({
    query: {
      // view: MainViewModeQueryObject.FAVORITE,
      ...query
    }
  });
};
export const updateQueryComikeMap = () => {
  const router = useRouter();
  const mapSearch = useMapSearchState().get();
  router.push({
    query: {
      // view: MainViewModeQueryObject.COMIKE_MAP,
      day: mapSearch.day,
      hall: mapSearch.hall,
      wcid: mapSearch.wcid,
      boothId: mapSearch.boothId,
      category: mapSearch.category,
      keyword: mapSearch.keyword,
    }
  });
};

/**
 * hash 変数
 * 先頭の #と/ の間に数値が入っていることがある。これを正規表現で取得し wcid キーとして格納する
 * スラッシュで split し、key=value の形式の文字列を Map として変換する
 * '#20017296/day=Day1/hall=e123/genreCode=500/scale=2'
 */
const convertToMapFromHash = (hash: string | undefined) => {
  if (!hash) return undefined;

  const hashParams = new Map<string, string>();
  const wcidMatch = hash.match(/^#(\d+)?\/(.*)$/);
  if (wcidMatch?.[2]) {
    if (wcidMatch[1]) {
      hashParams.set("wcid", wcidMatch[1]);
    }
    const parts = wcidMatch[2].split("/");
    for (const part of parts) {
      const [key, value] = part.split("=");
      if (key && value) {
        hashParams.set(key, value);
      }
    }
  }
  return hashParams;
};

/**
 * 旧WebCatalogのMapで使われていた引数
 * スラッシュで区切られた値を変換する
 */
const getMapSelectParamFromClassicWebCatalogHash = (hash: string | undefined) => {
  const hashParams = convertToMapFromHash(hash);
  if (hashParams === undefined) {
    return undefined;
  }
  return {
    day: getDayNumber(hashParams.get("day")),
    hall: hashParams.get("hall") as ComikeMapType, // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
    wcid: getNumber(hashParams.get("wcid")),
    boothId: getNumber(hashParams.get("boothId")),
    category: undefined,
    keyword: getSingleString(hashParams.get("keyword")),
    genreCode: hashParams.get("genreCode"),
  };
};

/**
 * クエリ値を返す。
 * 値がない場合はデフォルト値
 * @param query 
 * @param defaultValue 
 * @returns 
 */
const getCircleSearchStateFromQueryString = (query: LocationQuery, defaultValue:CircleSearchParam): CircleSearchParam => ({
  keyword: getSingleString(query.keyword) ?? defaultValue.keyword,
  genres: getMultiStringCommaSplit(query.genre) ?? defaultValue.genres,
  area: getSingleString(query.area) ?? defaultValue.area,
  blocks: getMultiStringCommaSplit(query.block) ?? defaultValue.blocks,
  day: query.day ? getNumber(query.day) : defaultValue.day,
  canUseElectronicPayment: getBool(query.pay) ?? defaultValue.canUseElectronicPayment,
  // favorite: getNumber(query["c.cmo"]) === 1, // お気に入りのみで絞込 1の場合に true
  favoriteColor: undefined,
  favoriteMemo: getBool(query.favoriteMemo) ?? defaultValue.favoriteMemo,
  sort: toSortOption(getSingleString(query.sort)) ?? defaultValue.sort, // 不正値・空文字は未指定として扱う (ADR-020)
  rankingScoreThreshold: clampRankingScoreThreshold(getNumber(query.rankingScoreThreshold) ?? defaultValue.rankingScoreThreshold),
  page: getNumber(query.page) ?? defaultValue.page,
  wcid: getNumber(query.wcid),
  isSearchCircleName: (getNumber(query.isSearchCircleName) ?? defaultValue.isSearchCircleName ? 1 : 0) > 0,
  isSearchAuthor: (getNumber(query.isSearchAuthor) ?? defaultValue.isSearchAuthor ? 1 : 0) > 0,
  isSearchProductInfo: (getNumber(query.isSearchProductInfo) ?? defaultValue.isSearchProductInfo ? 1 : 0) > 0,
  view: getCircleViewMode(getSingleString(query.view)) ?? defaultValue.view,
});

/**
 * CircleSearchStateをクエリパラメータから設定
 * デフォルト基準は初回のみで以降は最終設定を基準に上書きするようになった。 #33952
 */
const setCircleSearchStateFromQuery = (query: LocationQuery) => {
  const circleSearchState = useCircleSearchState();
  const nowValue = circleSearchState.get();
  circleSearchState.set(getCircleSearchStateFromQueryString(query, nowValue));
};

/**
 * BoothSearchStateをクエリパラメータから設定
 */
const setBoothSearchStateFromQuery = (query: LocationQuery) => {
  const boothSearchState = useBoothSearchState();
  boothSearchState.set({
    keyword: getSingleString(query.text),
  });
};

/**
 * MapSearchStateをクエリパラメータとハッシュから設定
 */
const setMapSearchStateFromQuery = (query: LocationQuery, hash: string) => {
  const mapSearchState = useMapSearchState();
  const stateFromHash = getMapSelectParamFromClassicWebCatalogHash(hash);
  const mapSearchStateDefault = mapSearchState.getDefaultValue();
  // hash よりも query を優先する
  mapSearchState.set({
    day: getNumber(query.day) ?? stateFromHash?.day,
    hall: getSingleString(query.hall) as ComikeMapType ?? stateFromHash?.hall, // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
    wcid: getNumber(query.wcid) ?? stateFromHash?.wcid,
    boothId: getNumber(query.boothId) ?? stateFromHash?.boothId,
    category: getSingleString(query.category) as MapSearchCategory ?? stateFromHash?.category ?? mapSearchStateDefault.category, // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
    keyword: getSingleString(query.keyword) ?? stateFromHash?.keyword,
    genreCode: getSingleString(query.genreCode) ?? stateFromHash?.genreCode,
  });
};

/**
 * 各画面用の State 構築 (2025年2月現在 app.vue から呼ばれている)
 */
export const setStateFromQueryString = () => {
  const route = useRoute();
  const query = route.query;
  const hash = route.hash;

  setCircleSearchStateFromQuery(query);
  setBoothSearchStateFromQuery(query);
  setMapSearchStateFromQuery(query, hash);
};

/**
 * URL文字列からクエリパラメータを解析してStateを設定
 * ログインのリダイレクトから戻ってきた時に initialRoute を参照した場合に利用する
 */
export const setStateFromUrl = (url: string) => {
  const urlObj = new URL(url, window.location.origin);
  const query = Object.fromEntries(urlObj.searchParams.entries()) as LocationQuery; // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
  const hash = urlObj.hash;

  setCircleSearchStateFromQuery(query);
  setBoothSearchStateFromQuery(query);
  setMapSearchStateFromQuery(query, hash);
};

/**
 * URLクエリに記載するべきかを判定する
 * デフォルト値と同じか同じ判定がされる要素に関してはURLクエリに記載しない。
 * 両方ともUndefinedの可能性があり、0,null,"","0" のような値の場合があるため考慮する。
 */ 
const isQueryAdd = (target: unknown, defaultValue?: unknown): boolean => {
  // target が undefined/null の場合は URL に含めない
  if (target === undefined || target === null) {
    return false;
  }
  // target と defaultValue が同じ場合は URL に含めない
  if (target === defaultValue) {
    return false;
  }
  // それ以外（target に値があり、defaultValue と異なる）は URL に含める
  return true;
};

/**
 * QueryString として設定するためのオブジェクトとして取得する 上記 setStateFromQueryString とペア
 */
const getLocationQuery = (): LocationQuery => {
  const circleSearchState = useCircleSearchState();
  const state = circleSearchState.get();
  const defaultValue = circleSearchState.getDefault();

  const q: LocationQuery = {};
  if (isQueryAdd(state?.keyword, defaultValue?.keyword)) {
    q["keyword"] = state.keyword ?? "";
  }
  if (state?.genres) {
    q["genre"] = state?.genres;
  }
  if (isQueryAdd(state?.day, defaultValue?.day)) {
    q["day"] = String(state.day);
  }
  if (isQueryAdd(state?.area, defaultValue?.area)) {
    q["area"] = state?.area;
  }
  if (state?.blocks) {
    q["block"] = state?.blocks;
  }
  if (isQueryAdd(state?.page, defaultValue?.page)) {
    q["page"] = String(state.page);
  }
  // if (state?.prev) {
  //   q["prev"] = state?.prev;
  // }
  if (state?.view !== undefined) {
    q["view"] = String(state.view);
  }
  if (state?.wcid !== undefined) {
    q["wcid"] = String(state.wcid);
  }
  if (isQueryAdd(state?.canUseElectronicPayment, defaultValue?.canUseElectronicPayment)) {
    q["pay"] = state?.canUseElectronicPayment ? "1" : "0";
  }
  if (isQueryAdd(state?.favoriteColor, defaultValue?.favoriteColor)) {
    q["favoriteColor"] = String(state.favoriteColor);
  }
  if (isQueryAdd(state?.favoriteMemo, defaultValue?.favoriteMemo)) {
    q["favoriteMemo"] = state?.favoriteMemo ? "1" : "0";
  }
  if (isQueryAdd(state?.sort, defaultValue?.sort)) {
    q["sort"] = String(state.sort);
  }
  if (isQueryAdd(state?.rankingScoreThreshold, defaultValue?.rankingScoreThreshold)) {
    q["rankingScoreThreshold"] = String(state.rankingScoreThreshold);
  }
  if (isQueryAdd(state?.isSearchCircleName, defaultValue?.isSearchCircleName)) {
    q["isSearchCircleName"] = state?.isSearchCircleName ? "1" : "0";
  }
  if (isQueryAdd(state?.isSearchAuthor, defaultValue?.isSearchAuthor)) {
    q["isSearchAuthor"] = state?.isSearchAuthor ? "1" : "0";
  }
  if (isQueryAdd(state?.isSearchProductInfo, defaultValue?.isSearchProductInfo)) {
    q["isSearchProductInfo"] = state?.isSearchProductInfo ? "1" : "0";
  }

  return q;
};

export const getPrintParam = (): PrintParam => {
  const route = useRoute();
  const printType = getNumber(route.params.printType); // printType という名称は type-[printType].vue に依存しているため params

  // パラメータの取得
  // パラメータ 名称は以下のリクエストクラスに合わせる
  // https://dev.azure.com/cosat/Circlems/_git/WebcatalogPublic?path=%2FSrc%2FComiketWebCatalog%2FCatalog.Web%2FControllers%2FPrintController.cs&version=GCffd83e16e35eda01003e120caee5ecd584cd41a3&line=101&lineEnd=131&lineStartColumn=1&lineEndColumn=15
  // 複数の値の区切り文字はURLエンコードを回避するため アンダースコア とする
  // URL QueryString 全パラメータ埋め込み例
  // /print-type-6?days=1,2&areas=e123,e456,e7,w12,c1&colors=1,2,3,4,5,6,7,8,9&prnColor=1&prnGenre=1&prnMemo=1&locale=ja
  const getDays = (): number[] => getMultiNumber(route.query.days) ?? [];
  const getAreas = (): MapArea[] => getMultiStringCommaSplit(route.query.areas) as MapArea[] ?? []; // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
  // halls=1,2 形式でホール番号を複数指定可能。半角数字文字列として保持する (JSON 側 hall 値と整合)。
  const getHalls = (): string[] => getMultiStringCommaSplit(route.query.halls) ?? [];
  const getColors = (): number[] => getMultiNumber(route.query.colors) ?? [];
  const prnColor = (): boolean => route.query.prnColor === "1";
  const prnGenre = (): boolean => route.query.prnGenre === "1";
  const prnMemo = (): boolean => route.query.prnMemo === "1";
  // 未指定時は従来どおり線を描画する
  const prnLines = (): boolean => route.query.prnLines !== "0";
  // 未指定時はオフ（既定）
  const prnMemoFit = (): boolean => route.query.prnMemoFit === "1";
  const getMemoLines = (): number => {
    const n = getNumber(route.query.memoLines);
    return clampMemoLines(n);
  };
  const getLocale = (): string | undefined => getSingleString(route.query.locale);

  return {
    printType: toPrintType(printType),
    printEachHall: route.query.printEachHall === "1",
    days: getDays(),
    areas: getAreas(),
    halls: getHalls(),
    colors: getColors(),
    prnColor: prnColor(),
    prnGenre: prnGenre(),
    prnMemo: prnMemo(),
    prnLines: prnLines(),
    prnMemoFit: prnMemoFit(),
    memoLines: getMemoLines(),
    locale: getLocale(),
  };
};

/**
 * URLエンコードされたQueryパラメータを生成する
 * @param p
 * @returns "days=1,2&areas=e123,e456,e7,w12,c1&colors=1,2,3,4,5,6,7,8,9&prnColor=1&prnGenre=1&prnMemo=1&locale=ja"
 */
export const getPrintQuery = (p: PrintParam): string => {
  const query = new URLSearchParams();

  // query.append("printType", String(p.printType))
  query.append("days", p.days.join(","));
  query.append("areas", p.areas.join(","));
  if (p.halls.length > 0) {
    // halls 未指定時は URL に含めない (後方互換性維持)
    query.append("halls", p.halls.join(","));
  }
  if (p.printEachHall) {
    // OFF 時は URL に含めない (後方互換性維持)
    query.append("printEachHall", "1");
  }
  query.append("colors", p.colors.join(","));
  query.append("prnColor", p.prnColor ? "1" : "0");
  query.append("prnGenre", p.prnGenre ? "1" : "0");
  query.append("prnMemo", p.prnMemo ? "1" : "0");
  query.append("prnLines", p.prnLines ? "1" : "0");
  query.append("prnMemoFit", p.prnMemoFit ? "1" : "0");
  query.append("memoLines", String(clampMemoLines(p.memoLines)));
  if (p.locale) {
    query.append("locale", p.locale);
  }

  return query.toString();
};

// TODO: URLベースで行うのではなく State + navigateTo で実装する
export const getCircleMapUrl = (circle: { wcid?: number, day?: number | null | undefined, area?: string | null | undefined }) => {
  const mainRoute = getNavigationRoute("COMIKE_MAP");
  return `${mainRoute}?day=${circle.day}&hall=${circle.area}&wcid=${circle.wcid}`;
  // return `${mainRoute}?view=${MainViewModeQueryObject.COMIKE_MAP}&day=${circle.day}&hall=${circle.area}&wcid=${circle.wcid}`;
};

// TODO: URLベースで行うのではなく State + navigateTo で実装する
export const getBoothMapUrl = (booth: Booth) => {
  const userModel = useUserModel();
  const canAccessMap = computed(() => isAuthorizedRole("COMIKE_MAP", userModel.userInfo().roles));
  const mainRoute: string | null | undefined = canAccessMap.value === true ? getNavigationRoute("COMIKE_MAP") : getNavigationRoute("COMIKE_BOOTH_MAP");
    return `${mainRoute}?&boothId=${booth.boothId}&hall=${booth.area}`;
};
